/**
 * Demo pairing requests for ?mock=1 (screenshots, visual tests): /pair/demo?mock=1&pair=<case>.
 * Cases: signed-out, open (the link's facts before a click opens it), pending (default), network,
 * awaiting-admin (a member opened it; an admin approves), approved, expired, denied, conflict,
 * cancelled, words, no-code, not-allowed, error, loading.
 */
import type { PairRequestView, SignInProvider } from '@orgus/protocol';
import type { UiError } from '@orgus/ui';

export type MockPairCase = 'signed-out' | 'open' | 'pending' | 'network' | 'awaiting-admin' | 'approved' | 'expired' | 'denied' | 'conflict' | 'cancelled' | 'words' | 'no-code' | 'not-allowed' | 'error' | 'loading';

export const MOCK_PAIR_CASES: readonly MockPairCase[] = ['signed-out', 'open', 'pending', 'network', 'awaiting-admin', 'approved', 'expired', 'denied', 'conflict', 'cancelled', 'words', 'no-code', 'not-allowed', 'error', 'loading'];

export function mockPairCase(search: string): MockPairCase {
  const c = new URLSearchParams(search).get('pair') as MockPairCase | null;
  return c && MOCK_PAIR_CASES.includes(c) ? c : 'pending';
}

export function mockPairView(c: MockPairCase, now = Date.now()): PairRequestView {
  const base: PairRequestView = {
    requestId: 'pair_01JC8Z4K7Q2M9XMAR3T5V6W8YZ',
    state: 'claimed',
    flow: 'browser',
    createdAt: now - 40_000,
    expiresAt: now + 9 * 60_000 + 20_000,
    fingerprint: 'K7Q2-9XMA-R3TV-HN4P',
    fingerprintHex: '9f86d081884c7d659a2feaa0c55ad015a3bf4f1b2b0b822cd15d6c15b0f00a08',
    machine: { hostname: 'ada-thinkpad', os: 'windows', osVersion: '11 23H2', arch: 'amd64', agentVersion: '0.4.0', user: 'ada' },
    harnesses: [
      { id: 'claude-code', version: '2.1.4', signedIn: true },
      { id: 'codex', version: '0.46.0', signedIn: false },
    ],
    differentNetwork: false,
    claimedBy: { memberId: 'mem_ada', name: 'Ada Lovelace' },
    org: { id: 'org_acme', name: 'Acme' },
    server: { domain: 'orgus.acme.com', keyFingerprint: 'M4TZ-QW7E-2KXB-9RLD' },
    mine: true,
    canDecide: true,
  };
  switch (c) {
    case 'network':
      return { ...base, differentNetwork: true };
    case 'awaiting-admin':
      return { ...base, canDecide: false, awaitingAdmin: true };
    case 'approved':
      return { ...base, state: 'approved', canDecide: false, machineId: 'm_demo1', machineName: 'Ada ThinkPad', decidedBy: { memberId: 'mem_ada', name: 'Ada Lovelace', at: now } };
    case 'expired':
      return { ...base, state: 'expired', canDecide: false, expiresAt: now - 60_000 };
    case 'denied':
      return { ...base, state: 'denied', canDecide: false, decidedBy: { memberId: 'mem_ada', name: 'Ada Lovelace', at: now } };
    case 'conflict':
      return { ...base, state: 'conflict', canDecide: false };
    case 'cancelled':
      return { ...base, state: 'cancelled', canDecide: false };
    case 'open':
      return { ...base, state: 'unclaimed', mine: false, canDecide: false, claimedBy: undefined };
    case 'words':
      return {
        ...base,
        state: 'unclaimed',
        flow: 'words',
        mine: false,
        canDecide: false,
        claimedBy: undefined,
      };
    default:
      return base;
  }
}

export const MOCK_NOT_ALLOWED: UiError = {
  what: 'This request was opened by someone else',
  why: 'Only the person who opened it on the computer, or an admin, can see it. An admin approves it.',
  next: 'Ask an admin.',
  status: 403,
};

export const MOCK_ERROR: UiError = {
  what: 'Could not load the pairing request.',
  why: 'The hub did not answer within 35 s.',
  next: 'Check that Orgus is running, then try again.',
};

/** The sign-in methods the demo signed-out card offers. */
export const MOCK_PAIR_PROVIDERS: SignInProvider[] = [
  { id: 'google', label: 'Google', startUrl: '/api/auth/google/start' },
  { id: 'microsoft', label: 'Microsoft', startUrl: '/api/auth/microsoft/start' },
  { id: 'github', label: 'GitHub', startUrl: '/api/auth/github/start' },
];
